import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, CheckCircle2, Circle } from "lucide-react";
import api from "@/lib/api";

function formatDayLabel(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch (_e) {
    return iso;
  }
}

function groupByDay(slots) {
  const map = {};
  for (const s of slots) {
    const key = (s.at || "").slice(0, 10);
    if (!map[key]) map[key] = [];
    map[key].push(s);
  }
  return map;
}

export default function SchedulePreviewCard({ refreshKey = 0, weeks = 2 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/autopilot/schedule-preview?weeks=${weeks}`);
        if (alive) setData(res.data);
      } catch (_e) {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshKey, weeks]);

  if (loading) {
    return <div className="text-sm text-[color:var(--text-3)]">Loading schedule preview…</div>;
  }
  if (!data || !data.slots?.length) {
    return (
      <div className="text-sm text-[color:var(--text-3)]" data-testid="schedule-preview-empty">
        Pick posting days and times to see your weekly schedule preview.
      </div>
    );
  }
  const grouped = groupByDay(data.slots);
  const days = Object.keys(grouped).sort();
  const filled = data.slots.filter((s) => s.has_post).length;
  return (
    <div className="space-y-2" data-testid="schedule-preview">
      <div className="flex items-center justify-between text-xs">
        <span className="text-[color:var(--text-3)] inline-flex items-center gap-1">
          <CalendarIcon className="h-3.5 w-3.5" />
          Next {data.weeks} weeks · {data.per_week} posts/week
        </span>
        <span className="font-mono-tabular text-[color:var(--text-2)]">{filled}/{data.slots.length} filled</span>
      </div>
      <ul className="space-y-2 max-h-[260px] overflow-auto pr-1">
        {days.map((d, idx) => (
          <motion.li
            key={d}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.03 }}
            className="flex items-start gap-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5"
            data-testid="schedule-day-row"
          >
            <div className="text-xs text-[color:var(--text-1)] font-medium min-w-[110px]">{formatDayLabel(grouped[d][0].at)}</div>
            <div className="flex flex-wrap gap-2 flex-1">
              {grouped[d].map((s, i) => (
                <span
                  key={i}
                  className={
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border " +
                    (s.has_post
                      ? "border-[color:rgba(47,231,161,0.45)] bg-[color:rgba(47,231,161,0.10)] text-[color:var(--success)]"
                      : "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-3)]")
                  }
                  data-testid="schedule-slot"
                >
                  {s.has_post ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                  {s.time}
                </span>
              ))}
            </div>
          </motion.li>
        ))}
      </ul>
    </div>
  );
}
